import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Icon, { type IconProps } from "@cloudscape-design/components/icon"
import ItemCard from "@cloudscape-design/components/item-card"
import Link from "@cloudscape-design/components/link"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { useNavigate } from "react-router-dom"
import { useMyAccess } from "@/hooks/queries/useMyAccess"
import { Notification } from "../../services/daeApi"
import { getNotificationActions } from "../../utils/notificationActions"
import "./NotificationCard.css"

interface NotificationCardProps {
  notification: Notification
  onMarkAsRead: (notificationId: string) => void
  isMarking?: boolean
  compact?: boolean
  /**
   * Give unread cards a background in the compact variant. Defaults to true.
   */
  highlightUnread?: boolean
  onNavigate?: () => void
}

const NotificationCard = ({
  notification,
  onMarkAsRead,
  compact,
  highlightUnread = true,
  onNavigate,
}: NotificationCardProps) => {
  const navigate = useNavigate()
  const { isExternal: isVendor } = useMyAccess()
  const isUnread = (notification.read_status || "UNREAD") === "UNREAD"

  const { primary, secondary } = getNotificationActions(notification, isVendor)
  const subject = notification.subject || `Study ${notification.study_id || "Update"}`

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return "Just now"
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`
      return date.toLocaleDateString()
    } catch {
      return dateString
    }
  }

  const getNotificationIcon = (
    type: string | undefined,
  ): { name: IconProps.Name; variant: IconProps.Variant } => {
    switch (type) {
      case "DTA_REJECTION":
      case "PROPOSAL_REJECTED":
      case "DTA_EXTRACTION_FAILED":
      case "PROTOCOL_EXTRACTION_FAILED":
      case "CONNECTION_REQUEST_REJECTED":
      case "EMAIL_DELIVERY_FAILURE":
      case "SECURITY_ALERT":
        return { name: "status-negative", variant: "error" }
      case "INGESTION_RUN_FAILED_REVIEW":
      case "DTA_INACTIVE":
        return { name: "status-warning", variant: "warning" }
      case "INGESTION_RUN_COMPLETED":
      case "PROPOSAL_ACCEPTED":
      case "DTA_ACTIVE":
      case "DTA_EXTRACTION_COMPLETE":
      case "PROTOCOL_EXTRACTION_COMPLETE":
      case "CONNECTION_REQUEST_APPROVED":
        return { name: "status-positive", variant: "success" }
      case "VENDOR_DATA_SUBMISSION":
        return { name: "upload-download", variant: "normal" }
      case "VENDOR_PROPOSAL_SUBMITTED":
      case "VENDOR_FEEDBACK_SUBMITTED":
      case "DAE_COMMENT_REPLY":
        return { name: "status-info", variant: "normal" }
      case "DTA_APPROVAL_REQUEST":
      case "DTA_READY_FOR_APPROVED":
        return { name: "status-pending", variant: "normal" }
      case "DTA_WORKFLOW_TRANSITION":
      case "DTA_RETURNED_FOR_REVIEW":
        return { name: "status-in-progress", variant: "normal" }
      case "DTA_READY_FOR_REVIEW":
        return { name: "view-full", variant: "normal" }
      case "VENDOR_ACCESS_GRANTED":
      case "VENDOR_CONNECTION_REQUEST":
      case "VENDOR_CONNECTION_CONFIGURED":
      case "VENDOR_ACCESSED_WORKSPACE":
        return { name: "user-profile", variant: "normal" }
      default:
        return { name: "file", variant: "normal" }
    }
  }

  const handleClick = () => {
    if (isUnread) onMarkAsRead(notification.notification_id)
    const target = primary || secondary
    if (target) {
      onNavigate?.()
      navigate(target.to)
    }
  }

  const handleLinkClick = (to: string) => {
    if (isUnread) onMarkAsRead(notification.notification_id)
    onNavigate?.()
    navigate(to)
  }

  const actions = (primary || secondary) && (
    <div className="notification-footer" onClick={(e) => e.stopPropagation()}>
      {primary && <Link onFollow={() => handleLinkClick(primary.to)}>{primary.label}</Link>}
      {secondary && <Link onFollow={() => handleLinkClick(secondary.to)}>{secondary.label}</Link>}
    </div>
  )

  if (compact) {
    return (
      <div className="notification-card" onClick={handleClick}>
        <ItemCard
          variant="embedded"
          style={
            isUnread && highlightUnread
              ? { root: { background: "var(--color-page-bg)" } }
              : undefined
          }
        >
          <SpaceBetween size="xxs">
            <Box variant="strong">{subject}</Box>
            {notification.message && <Box color="text-body-secondary">{notification.message}</Box>}
            {actions}
          </SpaceBetween>
        </ItemCard>
      </div>
    )
  }

  const { name, variant } = getNotificationIcon(notification.notification_type)

  return (
    <div className="notification-card" onClick={handleClick}>
      <ItemCard
        style={
          isUnread && highlightUnread ? { root: { background: "var(--color-page-bg)" } } : undefined
        }
      >
        <div className="notification-card-row">
          <Icon name={name} variant={variant} size="medium" />
          <div className="notification-card-content">
            <div className="notification-header">
              <Box variant="strong">{subject}</Box>
              {isUnread && <Badge color="blue">New</Badge>}
            </div>
            {notification.message && <Box color="text-body-secondary">{notification.message}</Box>}
            <Box color="text-status-inactive" fontSize="body-s">
              {formatDate(notification.sent_timestamp)}
            </Box>
            {actions}
          </div>
        </div>
      </ItemCard>
    </div>
  )
}

export default NotificationCard
